import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  EnvelopeSimple, CheckCircle, Warning, Trash, PaperPlaneTilt, Eye, EyeSlash,
  FloppyDisk, Info,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";

const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none text-sm";

const PRESETS = [
  { name: "Gmail (App Password)", host: "smtp.gmail.com", port: 587, tls: true,
    hint: "Requer App Password em https://myaccount.google.com/apppasswords (2FA obrigatório)." },
  { name: "Outlook / Hotmail", host: "smtp.office365.com", port: 587, tls: true,
    hint: "Se der erro, ative SMTP AUTH em https://outlook.live.com/mail/settings." },
  { name: "Zoho", host: "smtp.zoho.com", port: 587, tls: true,
    hint: "Use email empresarial do Zoho + senha de app." },
  { name: "Amazon SES", host: "email-smtp.us-east-1.amazonaws.com", port: 587, tls: true,
    hint: "Use SMTP username/password gerado no console SES." },
  { name: "SendGrid", host: "smtp.sendgrid.net", port: 587, tls: true,
    hint: "Username fixo 'apikey', senha = sua API Key completa." },
  { name: "Mailgun", host: "smtp.mailgun.org", port: 587, tls: true,
    hint: "Use SMTP username do domínio configurado." },
];

const emptyForm = {
  smtp_host: "", smtp_port: 587, smtp_username: "", smtp_password: "",
  smtp_from_email: "", smtp_from_name: "", smtp_use_tls: true,
};

export default function Smtp() {
  const [status, setStatus] = useState({ configured: false });
  const [form, setForm] = useState(emptyForm);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/smtp/status");
      setStatus(data);
      if (data.configured) {
        setForm({
          smtp_host: data.smtp_host || "",
          smtp_port: data.smtp_port || 587,
          smtp_username: data.smtp_username || "",
          smtp_password: "",  // nunca retornado pelo backend
          smtp_from_email: data.smtp_from_email || "",
          smtp_from_name: data.smtp_from_name || "",
          smtp_use_tls: data.smtp_use_tls ?? true,
        });
      }
    } catch (e) { console.warn(e); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPreset = (p) => {
    setForm((f) => ({ ...f, smtp_host: p.host, smtp_port: p.port, smtp_use_tls: p.tls }));
    toast.info(`Preset ${p.name} aplicado. ${p.hint}`);
  };

  const save = async () => {
    if (!form.smtp_host || !form.smtp_username || !form.smtp_password || !form.smtp_from_email) {
      return toast.error("Preencha host, usuário, senha e email remetente");
    }
    setSaving(true);
    try {
      await api.post("/smtp/config", {
        ...form,
        smtp_port: parseInt(form.smtp_port, 10),
      });
      toast.success("SMTP configurado — teste antes de usar em produção!");
      load();
    } catch (e) { toast.error(errMsg(e, "Erro ao salvar")); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Remover configuração SMTP? Emails de cobrança pararão de sair.")) return;
    await api.delete("/smtp/config");
    setForm(emptyForm);
    toast.success("SMTP removido");
    load();
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Informe um email destinatário");
    setTesting(true);
    try {
      const { data } = await api.post("/smtp/test", {
        to: testTo,
        subject: "Teste SMTP MercadoPagu ✅",
        body_html: `<h2 style="color:#eab308">Funcionou!</h2><p>Este é um email de teste enviado pelo seu SMTP através da plataforma <b>MercadoPagu</b>.</p><p>Enviado de: <b>${form.smtp_from_email}</b></p>`,
      });
      toast.success(`Email enviado! Provider: ${data.result?.provider}`);
    } catch (e) { toast.error(errMsg(e, "Erro no envio")); }
    finally { setTesting(false); }
  };

  return (
    <div className="fadeUp max-w-4xl">
      <div className="label-caps mb-2">Comunicação</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3 flex items-center gap-3">
        <EnvelopeSimple size={44} color="var(--accent)" weight="duotone" /> SMTP / Email
      </h1>
      <p className="text-white/60 mb-8">
        Configure seu servidor de email para enviar cobranças, notificações e campanhas
        com seu próprio domínio — sem passar pela plataforma.
      </p>

      {/* Status */}
      <div className="card-flat p-6 mb-6" data-testid="smtp-status-card">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-sm border flex items-center justify-center shrink-0 ${
            status.configured ? "border-[var(--success)] text-[var(--success)]" : "border-[var(--border)] text-white/40"
          }`}>
            {status.configured ? <CheckCircle size={26} weight="fill" /> : <Warning size={26} />}
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-xl" data-testid="smtp-state-label">
              {status.configured ? `Configurado: ${status.smtp_host}` : "Não configurado"}
            </div>
            <div className="text-xs text-white/50">
              {status.configured ? (
                <>Remetente: <b className="text-white/80">{status.smtp_from_name || ""} &lt;{status.smtp_from_email}&gt;</b> · Porta {status.smtp_port} {status.smtp_use_tls ? "(TLS)" : ""}</>
              ) : (
                "Configure seu SMTP para enviar emails com seu próprio domínio."
              )}
            </div>
          </div>
          {status.configured && (
            <button onClick={disconnect} data-testid="smtp-disconnect"
              className="btn-ghost text-xs text-[var(--danger)] flex items-center gap-1">
              <Trash size={14} /> Remover
            </button>
          )}
        </div>
      </div>

      {/* Presets */}
      <div className="card-flat p-4 mb-6">
        <div className="label-caps mb-3">Presets de servidores populares</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {PRESETS.map((p) => (
            <button key={p.name} onClick={() => applyPreset(p)}
              data-testid={`preset-${p.host}`}
              className="btn-ghost text-xs text-left px-3 py-2 flex flex-col">
              <span className="font-semibold">{p.name}</span>
              <span className="text-white/40 text-[10px] mt-0.5 truncate">{p.host}:{p.port}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="card-flat p-6" data-testid="smtp-form">
        <div className="font-display font-bold text-2xl mb-6">Credenciais SMTP</div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-caps block mb-2">Nome remetente</label>
            <input value={form.smtp_from_name} onChange={(e) => set("smtp_from_name", e.target.value)}
              data-testid="smtp-from-name" className={inp}
              placeholder="Meu Negócio LTDA" />
          </div>
          <div>
            <label className="label-caps block mb-2">Email remetente*</label>
            <input type="email" value={form.smtp_from_email}
              onChange={(e) => set("smtp_from_email", e.target.value)}
              data-testid="smtp-from-email" className={inp}
              placeholder="contato@meudominio.com" />
          </div>
          <div>
            <label className="label-caps block mb-2">Host SMTP*</label>
            <input value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)}
              data-testid="smtp-host" className={inp}
              placeholder="smtp.gmail.com" />
          </div>
          <div>
            <label className="label-caps block mb-2">Porta*</label>
            <input type="number" value={form.smtp_port}
              onChange={(e) => set("smtp_port", e.target.value)}
              data-testid="smtp-port" className={inp}
              placeholder="587" />
          </div>
          <div>
            <label className="label-caps block mb-2">Usuário SMTP*</label>
            <input value={form.smtp_username} onChange={(e) => set("smtp_username", e.target.value)}
              data-testid="smtp-username" className={inp}
              placeholder="normalmente é o próprio email ou 'apikey' no SendGrid" />
          </div>
          <div>
            <label className="label-caps block mb-2">Senha SMTP*</label>
            <div className="relative">
              <input type={showPwd ? "text" : "password"} value={form.smtp_password}
                onChange={(e) => set("smtp_password", e.target.value)}
                data-testid="smtp-password" className={inp + " pr-10"}
                placeholder={status.configured ? "••••••• (deixe em branco para manter)" : "app password ou senha do email"} />
              <button type="button" onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50">
                {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <input type="checkbox" checked={form.smtp_use_tls}
              onChange={(e) => set("smtp_use_tls", e.target.checked)}
              className="accent-[var(--accent)] w-5 h-5" data-testid="smtp-use-tls" />
            <span className="text-sm text-white/70">Usar TLS/STARTTLS (recomendado — porta 587)</span>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={save} disabled={saving} data-testid="smtp-save"
            className="btn-primary flex items-center gap-2">
            <FloppyDisk size={16} weight="bold" />
            {saving ? "Salvando..." : (status.configured ? "Atualizar" : "Salvar SMTP")}
          </button>
        </div>
      </div>

      {/* Teste */}
      {status.configured && (
        <div className="card-flat p-6 mt-6" data-testid="smtp-test-card">
          <div className="label-caps mb-2">Enviar teste</div>
          <div className="font-display font-bold text-2xl mb-4 flex items-center gap-2">
            <PaperPlaneTilt size={22} color="var(--success)" weight="fill" /> Testar envio
          </div>
          <div className="flex gap-3">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)}
              data-testid="smtp-test-to" className={inp}
              placeholder="Ex: você@gmail.com — envie um teste para o seu próprio email" />
            <button onClick={sendTest} disabled={testing} data-testid="smtp-test-send"
              className="btn-primary flex items-center gap-2 shrink-0">
              <PaperPlaneTilt size={16} weight="fill" /> {testing ? "Enviando..." : "Enviar"}
            </button>
          </div>
          <div className="mt-3 text-xs text-white/40 flex items-center gap-1">
            <Info size={12} /> Se falhar, a mensagem de erro exata do seu servidor SMTP aparecerá aqui em cima.
          </div>
        </div>
      )}
    </div>
  );
}
